import type { InfinitePaginatedData } from '../../api/types.js';
import {
  Table,
  TableBodyCell,
  TableContainer,
  TableContainerProps,
  TableProps,
  TableRow,
} from './TableComponents.jsx';

type InfinitelyPaginatedTableProps = TableProps & {
  columnsCount: number;
  data: InfinitePaginatedData<unknown>;
  isFetchingNextPage: boolean;
  isLoading?: boolean;
  placeholderRowsCount?: number;
  tableContainerProps?: TableContainerProps;
};

export const InfinitelyPaginatedTable = ({
  columnsCount,
  data,
  children,
  tableContainerProps,
  isFetchingNextPage,
  isLoading = false,
  placeholderRowsCount = 10,
  ...rest
}: InfinitelyPaginatedTableProps) => {
  return (
    <TableContainer {...tableContainerProps}>
      <Table {...rest}>
        {children}
        {isLoading && (
          <tbody>
            <PlaceholderRows
              columnsCount={columnsCount}
              rowsCount={placeholderRowsCount}
            />
          </tbody>
        )}
        <NextPagePlaceholderRows
          data={data}
          isFetchingNextPage={isFetchingNextPage}
          columnsCount={columnsCount}
        />
      </Table>
      {tableContainerProps?.children}
    </TableContainer>
  );
};

type NextPagePlaceholderRowsProps = {
  columnsCount: number;
  data: InfinitePaginatedData<unknown>;
  isFetchingNextPage: boolean;
};

const NextPagePlaceholderRows = ({
  columnsCount,
  data,
  isFetchingNextPage,
}: NextPagePlaceholderRowsProps) => {
  if (!isFetchingNextPage) {
    return null;
  }

  const lastPage = data.pages.at(-1);

  if (!lastPage) {
    return null;
  }

  const { pageNumber, itemsPerPage, totalItems } = lastPage;
  const nextPagePlaceholderRowsCount =
    totalItems >= (pageNumber + 1) * itemsPerPage
      ? itemsPerPage
      : totalItems % itemsPerPage;
  return (
    <tbody>
      <PlaceholderRows
        columnsCount={columnsCount}
        keyPrefix={`next-page-${pageNumber + 1}`}
        rowsCount={nextPagePlaceholderRowsCount}
      />
    </tbody>
  );
};

type PlaceholderRowsProps = {
  columnsCount: number;
  keyPrefix?: string;
  rowsCount: number;
};

const PlaceholderRows = ({
  columnsCount,
  keyPrefix = 'placeholder',
  rowsCount,
}: PlaceholderRowsProps) =>
  Array.from({ length: rowsCount }).map((_, rowIndex) => {
    const rowKey = `${keyPrefix}-row-${rowIndex}`;

    return (
      <TableRow key={rowKey}>
        {Array.from({ length: columnsCount }, (_, cellIndex) => (
          <TableBodyCell
            key={`${rowKey}-cell-${cellIndex}`}
            skeletonVariant={cellIndex === 0 ? 'checkbox' : 'default'}
            isLoading
          />
        ))}
      </TableRow>
    );
  });
